import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { MdRotateLeft } from "react-icons/md";
import { Link } from "@tanstack/react-router";
import { HStack, Wrap } from "../../../../styled-system/jsx";
import { css, cx } from "../../../../styled-system/css";
import { Button, buttonRecipe } from "../../core/basic/Button";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { SearchInput } from "../../fields/base/TextField";
import { Switch } from "../../fields/base/Switch";
import { BulkActionButton } from "../../table/BulkActionButton";
import { type AppColumns, createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { commaSearchFilterFn } from "../../table/filterFns";
import { resetTable } from "../../table/utils";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { formatNumber } from "../../../utils/formatNumber";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useDeletePrograms } from "../../../modules/programs/api/useDeletePrograms";
import { type Program, type ProgramStatus } from "../../../zodios/apiDefinitions/api/programs";

const columnHelper = createAppColumnHelper<Program>();

const searchFilterFn = commaSearchFilterFn<Program>((row) => row.name);

const truncateClassName = css({
  display: "block",
  maxWidth: "100%",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  textAlign: "start",
});

/** Shows the Active column; the pricing plan editor activates programs on its plan. Keep it memoized. */
interface ActiveProgram {
  id: number | null;
  isPending: boolean;
  /** Called with the program to activate, or null to deactivate. */
  onChange: (id: number | null) => void;
}

interface Props {
  /** undefined while loading. */
  programs: Program[] | undefined;
  isLoading: boolean;
  /** Heads the name column and names the bulk delete action. */
  title: string;
  persistKey: string;
  /** Makes the name a button that opens the program in place, instead of a link to its edit page. Keep it stable. */
  onOpenProgram?: (id: number) => void;
  activeProgram?: ActiveProgram;
  toolbarAction?: ReactNode;
}

/** Pricing programs: the /user/programs page and the pricing plan's Rules Engine tab. */
export const PricingProgramsTable = ({
  programs,
  isLoading,
  title,
  persistKey,
  onOpenProgram,
  activeProgram,
  toolbarAction = null,
}: Props) => {
  const { t } = useTranslation(["programs", "common"]);
  const dateFormatter = useDateFormatter("medium");
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");
  const deletePrograms = useDeletePrograms();

  const columns = useMemo(() => {
    const formatDate = (value: string | undefined) => (value ? dateFormatter.format(moment(value).toDate()) : "-");
    const statusLabels: Record<ProgramStatus, string> = {
      scheduled: t(($) => $.programs.list.Scheduled),
      active: t(($) => $.programs.list.Running),
      paused: t(($) => $.programs.list.Paused),
      finished: t(($) => $.programs.list.Finished),
      cancelled: t(($) => $.programs.list.Cancelled),
      error: t(($) => $.programs.list.Error),
    };

    const allColumns: AppColumns<Program> = [
      columnHelper.accessor("name", {
        header: title,
        size: 300,
        enableHiding: false,
        sortFn: "alphanumeric",
        cell: ({ getValue, row }) =>
          onOpenProgram ?
            <Button variant="link" className={truncateClassName} onPress={() => onOpenProgram(row.original.id)}>
              {getValue()}
            </Button>
          : <Link
              to="/user/programs/edit/$id"
              params={{ id: String(row.original.id) }}
              className={cx(buttonRecipe({ variant: "link" }), truncateClassName)}
            >
              {getValue()}
            </Link>,
      }),
      // A display column reading activeProgram rather than an `active` field joined into the rows:
      // new rows on every toggle would send the table back to page 1.
      ...(activeProgram ?
        [
          columnHelper.display({
            id: "active",
            header: t(($) => $.programs.list.Active),
            size: 100,
            enableResizing: false,
            cell: ({ row }) => {
              const isActive = row.original.id === activeProgram.id;
              return (
                <Switch
                  aria-label={t(($) => $.programs.list.Active)}
                  isSelected={isActive}
                  isDisabled={activeProgram.isPending}
                  onChange={() => activeProgram.onChange(isActive ? null : row.original.id)}
                />
              );
            },
          }),
        ]
      : []),
      columnHelper.accessor((row) => statusLabels[row.status], {
        id: "status",
        header: t(($) => $.programs.list.Status),
        size: 120,
        enableResizing: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((row) => row.last_run ?? undefined, {
        id: "last_run",
        header: t(($) => $.programs.list["Last Run"]),
        size: 140,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      columnHelper.accessor((row) => row.last_action ?? undefined, {
        id: "last_action",
        header: t(($) => $.programs.list["Last Action"]),
        size: 140,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      columnHelper.accessor("start_date", {
        header: t(($) => $.programs.header["Start Date"]),
        size: 140,
        enableResizing: false,
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      columnHelper.accessor((row) => row.end_date ?? undefined, {
        id: "end_date",
        header: t(($) => $.programs.header["End Date"]),
        size: 140,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      ...(hasMultiOrgRulesModuleEnabled ?
        [
          columnHelper.accessor((row) => row.org.name, {
            id: "org_name",
            header: t(($) => $.programs.list["Source Organization"]),
            size: 220,
            sortFn: "alphanumeric",
          }),
        ]
      : []),
      // After the dates, so a right-aligned number doesn't run into a left-aligned date.
      columnHelper.accessor("run_cnt", {
        header: t(($) => $.programs.list["# Runs"]),
        size: 100,
        enableResizing: false,
        meta: { justify: "end" },
        cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 0 }),
      }),
    ];
    return allColumns;
  }, [t, dateFormatter, hasMultiOrgRulesModuleEnabled, title, onOpenProgram, activeProgram]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey,
    initialState: { sorting: [{ id: "name", desc: false }] },
  });

  const table = useClientTable({
    data: isLoading ? undefined : programs,
    columns,
    getRowId: (row) => String(row.id),
    enableRowSelection: true,
    globalFilterFn: searchFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
    },
  });

  const selectedPrograms = table.getSelectedRowModel().rows.map((row) => row.original.id);

  return (
    <>
      <Wrap gap="medium" justify="end">
        {toolbarAction}
        <BulkActionButton
          selected={selectedPrograms}
          options={[
            {
              label: t(($) => $.programs.list["Delete {title}"], { title }),
              action: () => {
                deletePrograms.mutate(selectedPrograms, { onSuccess: () => table.resetRowSelection() });
              },
              confirmationMessage: (
                <p>
                  {t(($) => $.programs.list["Are you sure you want to delete the selected programs?"], {
                    count: selectedPrograms.length,
                  })}
                </p>
              ),
            },
          ]}
        />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};
